import React from 'react';

import type {BrandModel} from '~/lib/brandModels';

interface Props {
  model: BrandModel;
  isZhLocale: boolean;
}

/**
 * The 型號 band above the product count on a brand PLP.
 *
 * `intro_*` is the model's meta description and is capped at a sentence, so
 * reusing it here left the band saying almost nothing. The prose a shopper
 * reads lives in `detail_*` instead: the description Google shows must stay
 * short, the copy on the page should not.
 *
 * Everything renders into the DOM either way — the collapse below `lg` is
 * only a height clamp, so a crawler (and a reader who never taps) still gets
 * the full text.
 *
 * Below lg the order is name → copy → banner, stacked; the banner closes the
 * band so it stays clear of the mobile clamp, which an image would otherwise
 * fill. From lg the banner sits to the right of the copy at two fifths of
 * the column width, cropped taller so it stands as tall as the prose beside
 * it rather than as a strip against a wall of text.
 */
export default function BrandModelBand({model, isZhLocale}: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const [bannerFailed, setBannerFailed] = React.useState(false);
  const bodyId = React.useId();

  const detail = isZhLocale ? model.detail_zh : model.detail_en;
  // Older registry entries may carry only the one-line intro.
  const paragraphs = detail?.length
    ? detail
    : [isZhLocale ? model.intro_zh : model.intro_en].filter(Boolean);

  if (!model.banner && !paragraphs.length) return null;

  return (
    // Below lg everything stacks: name, copy, show-more, banner. From lg the
    // copy and the banner sit side by side, banner on the right at two
    // fifths of the column, so the prose keeps the wider share, and the two
    // are centred against each other so neither trails empty space.
    <div className="mx-5 mb-4 lg:mx-0 lg:flex lg:items-center lg:gap-8">
      <div className="lg:min-w-0 lg:flex-1">
        <h2 className="text-xl font-bold uppercase md:text-2xl">
          {model.name}
        </h2>

        <div
          id={bodyId}
          className={`relative ${
            expanded ? '' : 'max-h-44 overflow-hidden'
          } lg:max-h-none lg:overflow-visible`}
        >
          {paragraphs.map((paragraph) => (
            <p
              key={paragraph}
              className="mt-2 max-w-[70ch] text-sm text-slate-600 md:text-base"
            >
              {paragraph}
            </p>
          ))}

          {!expanded && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white lg:hidden"
            />
          )}
        </div>

        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((open) => !open)}
          className="mt-1 text-sm font-semibold text-hk-orange underline lg:hidden"
        >
          {expanded
            ? isZhLocale
              ? '收起'
              : 'Show less'
            : isZhLocale
            ? '展開全文'
            : 'Read more'}
        </button>
      </div>

      {model.banner && !bannerFailed && (
        // A fixed box with object-cover, so every model's banner occupies
        // exactly the same space whatever the source file measures. Below
        // lg it is 8:3 — the shape the banners are supplied in — and follows
        // the copy, outside the clamp above, which an image inside would
        // spend entirely on itself and hide the text. From lg it moves
        // beside the copy and crops to 5:3, which stands about as tall as a
        // model's prose. The subject sits centred in these banners, so the
        // centred crop trims dead margin rather than the helmet.
        <img
          src={model.banner}
          alt={model.name}
          loading="lazy"
          className="mt-3 aspect-[8/3] w-full rounded object-cover lg:mt-0 lg:aspect-[5/3] lg:w-2/5 lg:shrink-0"
          // A model whose image hasn't been supplied yet must not leave a
          // broken-image icon on a live brand page.
          onError={() => setBannerFailed(true)}
        />
      )}
    </div>
  );
}
